/**
 * hashToken.ts — read a capability token out of the URL FRAGMENT, then scrub it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY THE FRAGMENT AND NEVER A QUERY STRING. A query string is written verbatim
 * into load-balancer, WAF and nginx access logs on the very first page load, and
 * no client-side rewrite can retract it — a live credential would then be
 * sitting in plain text across three log estates. A fragment is never sent to
 * the server at all. The gateway enforces the same rule from its side: it 400s
 * these lanes when a query string is present.
 *
 * WHY WE STILL SCRUB IT. A fragment that stays in the address bar survives in
 * browser history, in a screen-share, and in the URL someone copies out to ask a
 * colleague for help. Reading it once and immediately replacing the entry is
 * what bounds the credential's life to this tab.
 *
 * Extracted from `pharmacy/PharmacyAuthPage` (2026-07-29) when the end-of-term
 * re-subscribe confirmation link became the second lane to need it — the two
 * lanes differ in audience, guard and failure copy, but the fragment discipline
 * is exactly the same and must not drift between them.
 */

/**
 * Pull a capability token out of `location.hash`.
 *
 * Accepts `#token=<value>` (and its `#t=` short form) as well as a bare
 * `#<value>`, so the shape of the emailed link can change without stranding
 * someone on a screen that says their link is broken. Returns null when the
 * fragment holds nothing usable.
 *
 * A MALFORMED FRAGMENT RETURNS NULL, IT DOES NOT THROW. `decodeURIComponent`
 * raises URIError on a stray percent (`#%`, or a link a mail client truncated
 * mid-escape), and both callers read this during their FIRST render — so an
 * escaping accident became a blank screen instead of the neutral "this link is
 * no longer valid" the pages are built to show. Null is the answer that lands on
 * that screen.
 */
export function readHashToken(hash: string): string | null {
  const raw = hash.replace(/^#/, '').trim();
  if (!raw) return null;
  if (raw.includes('=')) {
    const params = new URLSearchParams(raw);
    const named = params.get('token') ?? params.get('t');
    return named && named.trim() ? named.trim() : null;
  }
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/**
 * Replace the current history entry with the same path/query minus the
 * fragment. A no-op outside a browser (or where `replaceState` is unavailable)
 * so a caller never has to guard the environment itself.
 */
export function scrubHashFromAddressBar(): void {
  if (typeof window === 'undefined' || typeof window.history?.replaceState !== 'function') return;
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
}

/**
 * The QUERY-STRING sibling of `scrubHashFromAddressBar`, for a token that
 * arrived on the lane it should never have been on at all. A `?t=<token>`
 * arrival is IGNORED by every fragment-token page (the fragment is the only
 * read lane, and the gateway 400s a query-string token server-side) — but
 * ignoring it does not retract it: a live capability would sit in the address
 * bar, in browser history and in any URL copied out for help, for the whole
 * visit. This removes the token-shaped keys (`t` / `token` — the same names
 * `readHashToken` accepts) and ONLY those, in place, with no navigation.
 *
 * A SEGMENT FILTER, NOT a `URLSearchParams` round-trip: a round-trip
 * re-encodes every other segment (the `#faq` → `#faq=` lesson recorded in
 * journey/handoffScrub.ts), and everything that is not a token must survive
 * byte for byte. The fragment is preserved untouched — this helper's one job
 * is the query, and `scrubHashFromAddressBar` keeps its own semantics
 * (deliberately preserving `search`) for the fragment lane's callers.
 */
export function scrubTokenFromQueryString(): void {
  if (typeof window === 'undefined' || typeof window.history?.replaceState !== 'function') return;
  const raw = window.location.search.replace(/^\?/, '');
  if (!raw) return;
  const segments = raw.split('&');
  const kept = segments.filter((segment) => {
    const key = segment.split('=')[0]?.toLowerCase();
    return key !== 't' && key !== 'token';
  });
  if (kept.length === segments.length) return;
  const search = kept.length > 0 ? `?${kept.join('&')}` : '';
  window.history.replaceState(null, '', `${window.location.pathname}${search}${window.location.hash}`);
}
